import {
  buildCarePlanGeniePreview,
  CarePlanGenieFieldDefinition,
  CarePlanGenieQuestion,
} from "./carePlanGenieShared";

export interface UniversalPrecautionsGenieSectionValues {
  [key: string]: any;
}

export const UNIVERSAL_PRECAUTIONS_GENIE_FIELD_DEFINITIONS: Record<string, CarePlanGenieFieldDefinition> = {
  anyPrecautionsAlwaysApply: { key: "anyPrecautionsAlwaysApply", label: "Any precautions that always apply", type: "boolean", applyMode: "replace" },
  precautionsAlwaysApplyDetails: { key: "precautionsAlwaysApplyDetails", label: "Precautions always apply details", type: "text", applyMode: "append" },
  precautionsAlwaysApplyAssistanceRequired: { key: "precautionsAlwaysApplyAssistanceRequired", label: "Precautions always apply assistance required", type: "text", applyMode: "append" },
  anySpecialPrecautions: { key: "anySpecialPrecautions", label: "Any special precautions", type: "boolean", applyMode: "replace" },
  specialPrecautionsDetails: { key: "specialPrecautionsDetails", label: "Special precautions details", type: "text", applyMode: "append" },
  specialPrecautionsAssistanceRequired: { key: "specialPrecautionsAssistanceRequired", label: "Special precautions assistance required", type: "text", applyMode: "append" },
  anyAlternativeMethodOfVisitation: { key: "anyAlternativeMethodOfVisitation", label: "Any alternative method of visitation", type: "boolean", applyMode: "replace" },
  alternativeMethodOfVisitationDetails: { key: "alternativeMethodOfVisitationDetails", label: "Alternative method of visitation details", type: "text", applyMode: "append" },
  alternativeMethodOfVisitationAssistanceRequired: { key: "alternativeMethodOfVisitationAssistanceRequired", label: "Alternative method of visitation assistance required", type: "text", applyMode: "append" },
  canWearMask: { key: "canWearMask", label: "Can wear mask", type: "boolean", applyMode: "replace" },
  maskWearingDetails: { key: "maskWearingDetails", label: "Mask wearing details", type: "text", applyMode: "append" },
  maskWearingAssistanceRequired: { key: "maskWearingAssistanceRequired", label: "Mask wearing assistance required", type: "text", applyMode: "append" },
  safeToLeaveSanitizerWithResident: { key: "safeToLeaveSanitizerWithResident", label: "Safe to leave sanitizer with resident", type: "boolean", applyMode: "replace" },
  sanitizerDetails: { key: "sanitizerDetails", label: "Sanitizer details", type: "text", applyMode: "append" },
  sanitizerAssistanceRequired: { key: "sanitizerAssistanceRequired", label: "Sanitizer assistance required", type: "text", applyMode: "append" },
  upToDateWithVaccinations: { key: "upToDateWithVaccinations", label: "Up to date with vaccinations", type: "boolean", applyMode: "replace" },
  vaccinationsDetails: { key: "vaccinationsDetails", label: "Vaccinations details", type: "text", applyMode: "append" },
  vaccinationsAssistanceRequired: { key: "vaccinationsAssistanceRequired", label: "Vaccinations assistance required", type: "text", applyMode: "append" },
  anySharedMedicalEquipment: { key: "anySharedMedicalEquipment", label: "Any shared medical equipment", type: "boolean", applyMode: "replace" },
  sharedMedicalEquipmentDetails: { key: "sharedMedicalEquipmentDetails", label: "Shared medical equipment details", type: "text", applyMode: "append" },
  sharedMedicalEquipmentAssistanceRequired: { key: "sharedMedicalEquipmentAssistanceRequired", label: "Shared medical equipment assistance required", type: "text", applyMode: "append" },
  anyOtherPrecautions: { key: "anyOtherPrecautions", label: "Any other precautions", type: "boolean", applyMode: "replace" },
  otherPrecautionsDetails: { key: "otherPrecautionsDetails", label: "Other precautions details", type: "text", applyMode: "append" },
  otherPrecautionsAssistanceRequired: { key: "otherPrecautionsAssistanceRequired", label: "Other precautions assistance required", type: "text", applyMode: "append" },
};

export const UNIVERSAL_PRECAUTIONS_GENIE_QUESTIONS: CarePlanGenieQuestion[] = [
  {
    id: "standard",
    title: "Standard Precautions",
    prompt: "What routine infection-control or safety precautions should apply for this resident?",
    helperText: "Choose details and caregiver interventions that fit best. Genie will append them into the section notes.",
    editableFieldKeys: ["precautionsAlwaysApplyDetails", "precautionsAlwaysApplyAssistanceRequired", "specialPrecautionsDetails", "specialPrecautionsAssistanceRequired"],
    options: [
      {
        id: "standard_routine",
        label: "Routine standard precautions",
        description: "Use regular hand hygiene and standard infection-control practices.",
        groupLabel: "Precaution details",
        updates: {
          anyPrecautionsAlwaysApply: true,
          precautionsAlwaysApplyDetails: "Routine standard precautions should be followed during all care, including hand hygiene and appropriate cleaning practices.",
        },
      },
      {
        id: "standard_body_fluids",
        label: "Precautions needed for body fluid exposure",
        description: "Helpful when caregiver should anticipate contact with secretions, drainage, or incontinence.",
        groupLabel: "Precaution details",
        updates: {
          anyPrecautionsAlwaysApply: true,
          precautionsAlwaysApplyDetails: "Care tasks may involve exposure to body fluids, secretions, drainage, or incontinence and should be approached using routine universal precautions.",
        },
      },
      {
        id: "standard_skin_integrity",
        label: "Skin integrity or wound precautions matter",
        description: "Useful when wound care, skin issues, or open areas increase care considerations.",
        groupLabel: "Precaution details",
        updates: {
          anyPrecautionsAlwaysApply: true,
          precautionsAlwaysApplyDetails: "Skin issues, open areas, or wound-related care require consistent infection-control attention during routine support.",
        },
      },
      {
        id: "standard_special",
        label: "Special precautions needed",
        description: "Resident has additional infection-control or exposure considerations.",
        groupLabel: "Precaution details",
        updates: {
          anySpecialPrecautions: true,
          specialPrecautionsDetails: "Resident has special precaution needs that should be communicated clearly to caregiver and followed during care.",
        },
      },
      {
        id: "standard_special_isolation",
        label: "Enhanced or isolation-type precautions are relevant",
        description: "Use when added steps beyond routine precautions should be clearly documented.",
        groupLabel: "Precaution details",
        updates: {
          anySpecialPrecautions: true,
          specialPrecautionsDetails: "Resident may require enhanced precautions, isolation-type practices, or resident-specific infection-control steps beyond routine care.",
        },
      },
      {
        id: "standard_intervention_hand_hygiene",
        label: "Reinforce hand hygiene and PPE use",
        description: "Caregiver should consistently use standard infection-control practices.",
        groupLabel: "Caregiver interventions",
        updates: {
          anyPrecautionsAlwaysApply: true,
          precautionsAlwaysApplyAssistanceRequired: "Caregiver should consistently follow hand hygiene, glove use, PPE guidance, and routine cleaning practices during all care tasks.",
        },
      },
      {
        id: "standard_intervention_supplies",
        label: "Make sure supplies are available and used correctly",
        description: "Important when care depends on ready access to infection-control supplies.",
        groupLabel: "Caregiver interventions",
        updates: {
          anySpecialPrecautions: true,
          specialPrecautionsAssistanceRequired: "Caregiver should review and follow precaution guidance, make sure needed supplies are available, and report if infection-control materials are missing or inadequate.",
        },
      },
      {
        id: "standard_intervention_communicate",
        label: "Communicate precautions clearly across caregivers",
        description: "Use when consistency between caregiver is important.",
        groupLabel: "Caregiver interventions",
        updates: {
          anyPrecautionsAlwaysApply: true,
          precautionsAlwaysApplyAssistanceRequired: "Caregiver should communicate routine and resident-specific precautions clearly during handoff so care remains consistent across caregivers.",
        },
      },
    ],
  },
  {
    id: "visitation",
    title: "Visitation",
    prompt: "What should be documented about visitation alternatives or mask use?",
    helperText: "Choose details and caregiver interventions that fit best. Genie will append them into the section notes.",
    editableFieldKeys: ["alternativeMethodOfVisitationDetails", "alternativeMethodOfVisitationAssistanceRequired", "maskWearingDetails", "maskWearingAssistanceRequired"],
    options: [
      {
        id: "visit_alternative",
        label: "Alternative visitation methods are helpful",
        description: "Phone, video, or other alternate contact methods may be needed.",
        groupLabel: "Precaution details",
        updates: {
          anyAlternativeMethodOfVisitation: true,
          alternativeMethodOfVisitationDetails: "Alternative visitation methods such as phone or video contact may be helpful when in-person visits are limited or not practical.",
        },
      },
      {
        id: "visit_alternative_tolerance",
        label: "Remote visitation may work better than in-person visits at times",
        description: "Useful when fatigue, illness, or precautions affect visit tolerance.",
        groupLabel: "Precaution details",
        updates: {
          anyAlternativeMethodOfVisitation: true,
          alternativeMethodOfVisitationDetails: "Remote or alternative visitation may be preferred at times when health status, infection-control concerns, or resident tolerance make in-person visits less practical.",
        },
      },
      {
        id: "visit_mask_yes",
        label: "Can wear a mask with support",
        description: "Mask use is possible with cueing or setup.",
        groupLabel: "Precaution details",
        updates: {
          canWearMask: true,
          maskWearingDetails: "Resident is able to wear a mask when needed, with reminders or setup support as appropriate.",
        },
      },
      {
        id: "visit_mask_tolerance",
        label: "Mask tolerance may vary",
        description: "Comfort, cognition, or breathing may affect how long a mask can be worn.",
        groupLabel: "Precaution details",
        updates: {
          canWearMask: true,
          maskWearingDetails: "Mask tolerance may vary depending on breathing comfort, cognition, distress level, or how long mask use is required.",
        },
      },
      {
        id: "visit_intervention_setup",
        label: "Help arrange phone or video visits",
        description: "Caregivers may need to set up devices, timing, or connection.",
        groupLabel: "Caregiver interventions",
        updates: {
          anyAlternativeMethodOfVisitation: true,
          alternativeMethodOfVisitationAssistanceRequired: "Caregiver may need to help arrange, schedule, and support phone or video visitation so the resident can stay connected.",
        },
      },
      {
        id: "visit_intervention_mask_support",
        label: "Cue and support mask use when needed",
        description: "Provide a clean mask and gentle reminders.",
        groupLabel: "Caregiver interventions",
        updates: {
          canWearMask: true,
          maskWearingAssistanceRequired: "Caregiver may need to cue, offer a clean mask, support fit and comfort, and monitor tolerance during mask use when indicated.",
        },
      },
      {
        id: "visit_intervention_adjust",
        label: "Adjust visitation plans based on current status",
        description: "Use when health or precautions may change how visits happen.",
        groupLabel: "Caregiver interventions",
        updates: {
          anyAlternativeMethodOfVisitation: true,
          alternativeMethodOfVisitationAssistanceRequired: "Caregiver should adjust visitation support based on the resident's current condition, precaution needs, and tolerance for different visit methods.",
        },
      },
    ],
  },
  {
    id: "supplies",
    title: "Supplies and Equipment",
    prompt: "What should be documented about sanitizer, vaccinations, shared equipment, or other precautions?",
    helperText: "Choose details and caregiver interventions that fit best. Genie will append them into the section notes.",
    editableFieldKeys: ["sanitizerDetails", "sanitizerAssistanceRequired", "vaccinationsDetails", "vaccinationsAssistanceRequired", "sharedMedicalEquipmentDetails", "sharedMedicalEquipmentAssistanceRequired", "otherPrecautionsDetails", "otherPrecautionsAssistanceRequired"],
    options: [
      {
        id: "supply_sanitizer",
        label: "Sanitizer can be used safely",
        description: "Resident can have sanitizer available with appropriate oversight.",
        groupLabel: "Precaution details",
        updates: {
          safeToLeaveSanitizerWithResident: true,
          sanitizerDetails: "Hand sanitizer may be made available to the resident when it is considered safe and appropriate.",
        },
      },
      {
        id: "supply_sanitizer_limited",
        label: "Sanitizer access may need supervision",
        description: "Use when cognition, impulsivity, or misuse risk affects access.",
        groupLabel: "Precaution details",
        updates: {
          safeToLeaveSanitizerWithResident: false,
          sanitizerDetails: "Sanitizer or disinfecting supplies may require supervision rather than being left freely available due to safety, cognition, or misuse concerns.",
        },
      },
      {
        id: "supply_vaccinations",
        label: "Vaccination status should be tracked",
        description: "Immunization follow-up is part of routine care planning.",
        groupLabel: "Precaution details",
        updates: {
          upToDateWithVaccinations: true,
          vaccinationsDetails: "Resident's vaccination status should be reviewed and updated according to current medical guidance and records.",
        },
      },
      {
        id: "supply_vaccinations_followup",
        label: "Vaccination records or follow-up may be incomplete",
        description: "Useful when caregiver need to confirm status or coordinate next steps.",
        groupLabel: "Precaution details",
        updates: {
          upToDateWithVaccinations: false,
          vaccinationsDetails: "Vaccination records, status, or follow-up may need review so the care team can confirm what is current and what still needs coordination.",
        },
      },
      {
        id: "supply_shared_equipment",
        label: "Shared medical equipment needs cleaning precautions",
        description: "Equipment used across settings or people should be cleaned consistently.",
        groupLabel: "Precaution details",
        updates: {
          anySharedMedicalEquipment: true,
          sharedMedicalEquipmentDetails: "Shared medical equipment should be cleaned and handled according to routine infection-control practices.",
        },
      },
      {
        id: "supply_shared_equipment_labeling",
        label: "Shared equipment needs clear cleaning workflow",
        description: "Use when reuse, storage, or transport can create confusion.",
        groupLabel: "Precaution details",
        updates: {
          anySharedMedicalEquipment: true,
          sharedMedicalEquipmentDetails: "Shared equipment should have clear cleaning, storage, and reuse expectations so caregiver know when the device is ready for the next use.",
        },
      },
      {
        id: "supply_other",
        label: "Other precautions should be listed",
        description: "Additional resident-specific precaution needs should be documented.",
        groupLabel: "Precaution details",
        updates: {
          anyOtherPrecautions: true,
          otherPrecautionsDetails: "Resident has additional precaution needs that should be documented clearly for caregiver reference.",
        },
      },
      {
        id: "supply_other_environment",
        label: "Environment setup affects precaution follow-through",
        description: "Useful when room setup, storage, or workflow impacts safety.",
        groupLabel: "Precaution details",
        updates: {
          anyOtherPrecautions: true,
          otherPrecautionsDetails: "Precaution follow-through may depend on room setup, supply placement, equipment handling, or other environmental workflow needs.",
        },
      },
      {
        id: "supply_intervention_sanitizer",
        label: "Monitor sanitizer and disinfecting supply use",
        description: "Caregivers should supervise safe use and replenishment.",
        groupLabel: "Caregiver interventions",
        updates: {
          sanitizerAssistanceRequired: "Caregiver should monitor safe use of sanitizer or disinfecting supplies, restock items as needed, and provide reminders or direct assistance when appropriate.",
        },
      },
      {
        id: "supply_intervention_vaccinations",
        label: "Coordinate records, reminders, and appointments",
        description: "Use when caregiver support is needed for vaccination follow-through.",
        groupLabel: "Caregiver interventions",
        updates: {
          vaccinationsAssistanceRequired: "Caregiver may need to help coordinate records, reminders, appointments, and communication related to vaccination follow-up.",
        },
      },
      {
        id: "supply_intervention_equipment",
        label: "Clean shared equipment between uses",
        description: "Important when devices move between residents, rooms, or caregiver.",
        groupLabel: "Caregiver interventions",
        updates: {
          anySharedMedicalEquipment: true,
          sharedMedicalEquipmentAssistanceRequired: "Caregiver should clean shared equipment between uses, follow device-specific precautions, and document concerns if cleaning steps cannot be completed as expected.",
        },
      },
      {
        id: "supply_intervention_other",
        label: "Review added resident-specific precautions during care",
        description: "Helps keep custom precautions consistent across caregivers.",
        groupLabel: "Caregiver interventions",
        updates: {
          anyOtherPrecautions: true,
          otherPrecautionsAssistanceRequired: "Caregiver should review and follow any added resident-specific precaution instructions as part of routine care and handoff communication.",
        },
      },
    ],
  },
];

export const buildUniversalPrecautionsGeniePreview = (
  currentValues: Record<string, any>,
  selectedOptions: Record<string, string[]>,
  questionDrafts: Record<string, Record<string, string>>,
) => buildCarePlanGeniePreview(
  UNIVERSAL_PRECAUTIONS_GENIE_FIELD_DEFINITIONS,
  UNIVERSAL_PRECAUTIONS_GENIE_QUESTIONS,
  currentValues,
  selectedOptions,
  questionDrafts,
);
